import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  RefreshCw,
  RotateCcw,
  Loader2,
  CalendarDays,
  AlertTriangle,
  Download,
  FileSpreadsheet,
  Fingerprint,
} from 'lucide-react';
import { leaveService } from '@/services/leave.service';
import { getErrorMessage } from '@/lib/errors';
import { toast } from 'sonner';

function ConfirmDialog({
  open,
  title,
  description,
  onConfirm,
  onCancel,
  loading,
}: {
  open: boolean;
  title: string;
  description: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={onConfirm} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** "31 Dec 2026, 11:55 pm" in IST — the saved file's time, as the year-end job means it. */
function formatSavedAt(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const SETTINGS_KEY = ['leave', 'settings'] as const;

function AutoCancelOnPunchCard() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => leaveService.getSettings(),
  });

  const updateMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      leaveService.updateSettings({ auto_cancel_leave_on_punch: enabled }),
    onSuccess: (data) => {
      queryClient.setQueryData(SETTINGS_KEY, data);
      toast.success(
        data.auto_cancel_leave_on_punch
          ? 'Auto-cancel leave on punch turned on'
          : 'Auto-cancel leave on punch turned off',
      );
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const title = 'Auto-cancel leave on punch';

  return (
    <div className="rounded-xl border border-slate-200 dark:border-border p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Fingerprint className="w-4 h-4 text-rose-600" />
        <h3 className="font-semibold text-slate-800 dark:text-slate-100">Leave &amp; Punch</h3>
      </div>
      {settingsQuery.isLoading ? (
        <div className="h-12 animate-pulse rounded-lg bg-muted/50" />
      ) : settingsQuery.isError ? (
        <div className="flex items-center gap-3 text-sm text-destructive">
          Failed to load setting.
          <Button size="sm" variant="outline" onClick={() => void settingsQuery.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <label className="flex cursor-pointer select-none items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              If an employee punches in on a day covered by an approved full-day leave (paid or
              unpaid), the whole leave is cancelled. Paid leave credit is given back; unpaid leave is
              only cancelled. Half-day leave is not affected.
            </p>
          </div>
          <Switch
            checked={settingsQuery.data?.auto_cancel_leave_on_punch ?? false}
            onChange={(v) => updateMutation.mutate(v)}
            disabled={updateMutation.isPending}
            aria-label={title}
          />
        </label>
      )}
    </div>
  );
}

export function LeaveCreditSection() {
  const today = new Date();
  const [accrueMonth, setAccrueMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
  );
  const [resetYear, setResetYear] = useState(today.getFullYear() - 1);
  const [confirmAccrue, setConfirmAccrue] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [exportYear, setExportYear] = useState<number | null>(null);

  const yearsQuery = useQuery({
    queryKey: ['leave', 'year-exports'],
    queryFn: () => leaveService.listYearExports(),
  });
  const years = yearsQuery.data ?? [];
  // Newest year by default, until one is picked.
  const selectedExport = years.find((y) => y.year === exportYear) ?? years[0] ?? null;

  const accrueMutation = useMutation({
    mutationFn: () => {
      const [y, m] = accrueMonth.split('-');
      const date = new Date(Number(y), Number(m) - 1, 1);
      return leaveService.runAccrue(date.toISOString().split('T')[0]);
    },
    onSuccess: (result) => {
      toast.success(`Accrual complete — ${result.inserted} credit entries added`);
      setConfirmAccrue(false);
    },
    onError: () => {
      toast.error('Accrual failed. Check server logs.');
      setConfirmAccrue(false);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => leaveService.runYearlyReset(resetYear),
    onSuccess: (result) => {
      toast.success(`Yearly reset complete — ${result.reset} balances started fresh for ${resetYear + 1}`);
      setConfirmReset(false);
    },
    onError: () => {
      toast.error('Yearly reset failed. Check server logs.');
      setConfirmReset(false);
    },
  });

  const downloadMutation = useMutation({
    mutationFn: (year: number) => leaveService.downloadYearExportXlsx(year),
    onSuccess: (_, year) => toast.success(`Leave data ${year} downloaded`),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  return (
    <div className="space-y-6">
      <AutoCancelOnPunchCard />

      {/* Monthly Accrual */}
      <div className="rounded-xl border border-slate-200 dark:border-border p-5 space-y-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-teal-600" />
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">Monthly Credit Accrual</h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Adds monthly credits to every active employee whose credit start date is on or before the
          selected month. Idempotent — running it twice for the same month is safe.
        </p>
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Credit Month
            </label>
            <input
              type="month"
              value={accrueMonth}
              onChange={(e) => setAccrueMonth(e.target.value)}
              className="h-9 px-3 rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>
          <Button
            onClick={() => setConfirmAccrue(true)}
            disabled={accrueMutation.isPending}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {accrueMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Run Accrual
          </Button>
        </div>
      </div>

      {/* Yearly Reset */}
      <div className="rounded-xl border border-slate-200 dark:border-border p-5 space-y-4">
        <div className="flex items-center gap-2">
          <RotateCcw className="w-4 h-4 text-amber-600" />
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">Yearly Balance Reset</h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Starts the next year fresh for every employee: unused days of the selected year are not
          carried forward. January&apos;s credit is kept. Runs automatically on 1 January — the closed
          year stays downloadable below. Idempotent — safe to run multiple times for the same year.
        </p>
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Reset Year
            </label>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-slate-400" />
              <input
                type="number"
                value={resetYear}
                min={2020}
                max={today.getFullYear()}
                onChange={(e) => setResetYear(Number(e.target.value))}
                className="h-9 w-28 px-3 rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setConfirmReset(true)}
            disabled={resetMutation.isPending}
            className="border-amber-200 text-amber-700 hover:bg-amber-50"
          >
            {resetMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RotateCcw className="w-4 h-4 mr-2" />
            )}
            Run Yearly Reset
          </Button>
        </div>
      </div>

      {/* Past Year Leave Data */}
      <div className="rounded-xl border border-slate-200 dark:border-border p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">Past Year Leave Data</h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Excel of a year&apos;s leave balances and monthly credits. Saved automatically on 31 December
          at 11:55 PM, before the new year starts fresh.
        </p>
        {yearsQuery.isLoading ? (
          <div className="h-9 w-64 animate-pulse rounded-lg bg-muted/50" />
        ) : yearsQuery.isError ? (
          <div className="flex items-center gap-3 text-sm text-destructive">
            Failed to load years.
            <Button size="sm" variant="outline" onClick={() => void yearsQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : !selectedExport ? (
          <p className="text-sm text-muted-foreground">No leave data yet.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Year</label>
                <select
                  value={selectedExport.year}
                  onChange={(e) => setExportYear(Number(e.target.value))}
                  className="h-9 w-28 px-3 rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                >
                  {years.map((y) => (
                    <option key={y.year} value={y.year}>{y.year}</option>
                  ))}
                </select>
              </div>
              <Button
                onClick={() => downloadMutation.mutate(selectedExport.year)}
                disabled={downloadMutation.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {downloadMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download Excel
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedExport.saved_at
                ? `Saved automatically on ${formatSavedAt(selectedExport.saved_at)}`
                : 'Not saved yet — downloads the live data'}
            </p>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmAccrue}
        title="Run Monthly Credit Accrual?"
        description={`This will add monthly credits for all eligible employees for ${accrueMonth}. Existing entries for this month will be skipped.`}
        onConfirm={() => accrueMutation.mutate()}
        onCancel={() => setConfirmAccrue(false)}
        loading={accrueMutation.isPending}
      />

      <ConfirmDialog
        open={confirmReset}
        title={`Start ${resetYear + 1} fresh?`}
        description={`Unused ${resetYear} leave days will not be carried into ${resetYear + 1} for any employee. ${resetYear} data stays downloadable as Excel. This cannot be undone.`}
        onConfirm={() => resetMutation.mutate()}
        onCancel={() => setConfirmReset(false)}
        loading={resetMutation.isPending}
      />
    </div>
  );
}
